import React, {FormEvent, useState} from 'react';
import {AppTheme} from '../../../appTheme';
import { Button, Container, Grid, Group, Paper, Text, Title, Divider, Badge, useMantineTheme, Alert } from '@mantine/core';
import {connect} from 'react-redux';
import {CommonState} from '../../../redux';
import {itemStore} from '../../../redux/entities/inventory/item';
import {categoryStore} from '../../../redux/entities/inventory/category';
import {loadInventoryManagementData} from "../../../redux/stateResponses/inventoryManagement";
import {Dispatch, bindActionCreators} from "redux";
import {getProductSelectionState, submitPublicOrder} from "../../../api/inventory/inventoryApi";
import {useMount} from "../../../hooks/useMount";
import {AxiosError} from "axios";
import {getErrorResponseMessage, handleAxiosError} from "../../../util/http";
import {CenteredSpinner} from "../../../components/util/widgets/CenteredSpinner/CenteredSpinner";
import {CenteredErrorMessage} from "../../../components/util/widgets/CenteredErrorMessage/CenteredErrorMessage";
import OrderGrid from "../../private/Configuration/OrderManagement/components/OrderGrid";
import {loadProductSelectionStateData} from "../../../redux/stateResponses/productSelectionState";
import {Formik, Form, FormikErrors} from 'formik';
import {WithdrawalSchema} from './WithdrawalSchema';
import { makeOrder } from '../../../redux/factory/inventory/order';
import { Order } from '../../../redux/entities/inventory/order';
import styles from './LandingStyles.module.scss';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../router/RoutePaths';
import OrderForm from '../../../components/layout/Order/OrderForm';

// Removed BasicOrderItem; using centralized helpers from OrderSchema

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

function Landing(props: Props) {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState<string | undefined>();
  const [submittedOrderData, setSubmittedOrderData] = useState<Order | null>(null);
  const [submissionTime, setSubmissionTime] = useState<Date | undefined>();
  const navigate = useNavigate();

  // Theme available for potential responsive behavior
  const theme = useMantineTheme();

  const {itemsFromStore, categories} = props;

  useMount(async () => {
    try {
      await props.actions.loadInventoryData();
    } catch (e: AxiosError | any) {
      setErrorMessage(handleAxiosError(e, {connectionMsg: 'Failed to load inventory'}));
    }
    setLoading(false);
  });

  const processSubmit = async (
    e: FormEvent<HTMLFormElement>,
    association: Order,
    validate: (values: Order) => Promise<FormikErrors<Order>>,
    formikHandleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void,
    setTouched: (touched: any, shouldValidate?: boolean) => void) => {
    console.log('submitting')
    setIsSubmitting(true);
    setHasSubmitted(true);
    setSubmissionErrorMessage('');
    e.persist();
    e.preventDefault();
    const errors = await validate(association as any);
    if (Object.values(errors).length !== 0) {
      console.log('errprs found');
      console.log(JSON.stringify(errors));
      console.log(JSON.stringify(`${association.houseId} \r\n ${association.programId}`));
      // Mark all relevant fields as touched so errors appear on inputs
      setTouched({
        consumerInformation: {
          name: true,
          jobTitle: true,
          emailAddress: true,
          phoneNumber: true,
          programOrDepartment: true,
          houseOrNeighborhood: true,
          everythingFound: true,
          everythingFoundComment: true,
          additionalComment: true,
        },
        programId: true,
        houseId: true,
        orderItems: true,
      }, false);
      formikHandleSubmit(e);
      window.scrollTo(0, -400);
      setIsSubmitting(false);
    } else {
      try {
        const isFromWarehouse = !!association.isFromWarehouse;
        const processedOrderItems = association.orderItems
          .filter(oi => {
            const hasQuantity = (oi?.storeQuantity ?? 0) > 0 || (oi?.warehouseQuantity ?? 0) > 0;
            const hasOptions = (oi?.itemOptions?.length > 0) && oi.itemOptions.some(o => o.quantity > 0);
            return hasQuantity || hasOptions;
          })
          .map(oi => {
            // For the public landing page, we consolidate the selected quantity into the chosen source (Store or Warehouse)
            const quantity = oi.storeQuantity || oi.warehouseQuantity || 0;
            return {
              ...oi,
              storeQuantity: isFromWarehouse ? 0 : quantity,
              warehouseQuantity: isFromWarehouse ? quantity : 0
            };
          });

        const request: Order = {
          ...association,
          orderItems: processedOrderItems
        };
        console.log('submitting order:', request);
        await submitPublicOrder(request);
        // Navigate to confirmation page with countdown
        navigate(RoutePaths.publicPaths.orderSubmitted, { replace: true });
      } catch (e: AxiosError | any) {
        setSubmissionErrorMessage(getErrorResponseMessage(e));
      }
    }
    setIsSubmitting(false);
  };


  return (
    <Container className={styles['container']}>
      <Paper
        radius="lg"
        p="xl"
        shadow="md"
        className={styles['card']}
      >
        <Group position="apart" align="center" className={styles['headerGroup']}>
          <div>
            <Title order={2} className={styles['titleGreen']}>
              Home of the Innocents Self-Service Portal
            </Title>
            <Text size="sm" color="dimmed">
              Select retrieved items and provide your contact information.
            </Text>
          </div>
        </Group>

        <Divider my="md"/>

        <Formik<Order>
          initialValues={makeOrder()}
          validationSchema={WithdrawalSchema}
          validateOnBlur={false}
          validateOnChange={hasSubmitted}
          onSubmit={(values) => {
            // eslint-disable-next-line no-console
            console.log({
              consumerInformation: values.consumerInformation,
              orderItems: values.orderItems
            });
          }}
        >
          {({values, setFieldValue, handleSubmit, validateForm, errors, touched, setTouched}) => {

            const flattenErrorMessages = (err: any): string[] => {
              const collected: string[] = [];
              const walk = (e: any) => {
                if (!e) return;
                if (typeof e === 'string') collected.push(e);
                else if (Array.isArray(e)) e.forEach(walk);
                else if (typeof e === 'object') Object.values(e).forEach(walk);
              };
              walk(err);
              // dedupe and keep order
              return Array.from(new Set(collected.filter(Boolean)));
            };

            const validationMessages = flattenErrorMessages(errors);

            return (
              <Form onSubmit={(e) => processSubmit(e, values, validateForm, handleSubmit, setTouched)}>
                {loading ? (
                  <CenteredSpinner />
                ) : errorMessage ? (
                  <CenteredErrorMessage message={errorMessage} />
                ) : (
                  <>
                    <OrderForm items={itemsFromStore} categories={categories} title="Items to Withdraw" />
                    <Grid gutter="xl">
                      <Grid.Col span={12} md={12}>
                        <Group position="right">
                          <Button type="submit" size="md" className={styles['submitButton']} loading={isSubmitting} disabled={isSubmitting}>Submit request</Button>
                        </Group>
                      </Grid.Col>
                      {(submissionErrorMessage || (hasSubmitted && validationMessages.length > 0)) && (
                        <Grid.Col span={12} md={12}>
                          <Alert color="red" variant="light" title="Please review the following:">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {hasSubmitted && validationMessages.map((m, idx) => (
                                <Text key={idx} size="sm" color="red">
                                  • {m}
                                </Text>
                              ))}
                              {submissionErrorMessage && (
                                <Text size="sm" color="red">
                                  • {submissionErrorMessage}
                                </Text>
                              )}
                            </div>
                          </Alert>
                        </Grid.Col>
                      )}
                    </Grid>
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </Container>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    loadInventoryData: loadProductSelectionStateData
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  itemsFromStore: itemStore.selectors.unarchivedItems(state),
  categories: categoryStore.selectors.getAsArray(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(Landing);
